import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

/**
 * GardenSettingsContext - Manages garden settings globally
 *
 * Features:
 * - Global access to garden metadata (from wizard)
 * - localStorage persistence for offline access
 * - Backend synchronization support (future)
 * - Used by: GardenCanvas, Plant filtering, Settings dialog
 *
 * Settings Shape:
 * {
 *   // Step 1: Garden Basics
 *   name: string,             // Garden name (1-200 chars, default: "Mein Garten")
 *   length: number,           // Garden length in meters (3-100)
 *   width: number,            // Garden width in meters (3-100)
 *   soilType: string,         // 'Sand', 'Lehm', 'Ton', 'Löss', 'Mischboden'
 *   phValue: number,          // pH value (4.0-8.0)
 *   drainage: string,         // 'Gut', 'Mittel', 'Schlecht'
 *   orientation: string,      // 'N', 'NO', 'O', 'SO', 'S', 'SW', 'W', 'NW'
 *   sunlight: string,         // 'Vollsonnig', 'Sonnig', 'Halbschattig', 'Schattig'
 *   zipCode: string,          // Postal code (optional)
 *   city: string,             // City name (optional)
 *
 *   // Step 2: Background Image (optional)
 *   backgroundImage: {
 *     dataUrl: string,        // Base64 encoded image data
 *     transparency: number,   // 0-100
 *     position: {x, y},       // Position on canvas
 *     rotation: number,       // Rotation angle in degrees
 *     calibration: {          // Scale calibration
 *       scale: number,        // Pixels per meter
 *       isCalibrated: boolean,
 *       realDistance: number,
 *       pixelDistance: number
 *     },
 *     locked: boolean         // Always true - image locked in GardenCanvas
 *   } | null,
 *
 *   // Step 3: Garden Outline (mandatory in MVP)
 *   outline: {
 *     vertices: [{x, y}, ...], // Polygon vertices
 *     isClosed: boolean        // Should always be true after wizard completion
 *   } | null,
 *
 *   // Step 4: Height Differences (optional)
 *   heightDifferences: {
 *     enabled: boolean,
 *     zones: [{
 *       id: string,
 *       vertices: [{x, y}, ...],
 *       height: number,        // Height in cm (-200 to +200)
 *       color: string,         // Color based on height
 *       isClosed: boolean,
 *       createdAt: string
 *     }]
 *   } | null
 * }
 */

// Exported so other contexts (e.g. LayerContext) can consume it null-safely via
// useContext without the throwing useGardenSettings guard — they degrade to
// localStorage-only behavior when no GardenSettingsProvider is present (#466).
export const GardenSettingsContext = createContext(null);

// localStorage key for garden settings persistence
const STORAGE_KEY = 'gartenplaner_garden_settings';

// localStorage key for active garden ID (backend persistence)
const ACTIVE_GARDEN_ID_KEY = 'gartenplaner_active_garden_id';

// Default settings (null values indicate not configured)
const DEFAULT_SETTINGS = {
  // Step 1: Garden Basics
  name: 'Mein Garten', // Default garden name
  length: null,
  width: null,
  soilType: null,
  phValue: 7.0, // Neutral pH as default
  drainage: null,
  orientation: null,
  sunlight: null,
  zipCode: null,
  city: null,
  // Geo-Koordinaten aus dem PLZ-Lookup (#604) — Basis für den Sonnenstand in
  // der 3D-Ansicht. Additiv persistiert (Muster wie northAngle/layers).
  latitude: null,
  longitude: null,

  // Step 2: Background Image (optional)
  backgroundImage: null,

  // Step 3: Garden Outline (mandatory in MVP)
  outline: null,

  // Step 4: Height Differences (optional)
  heightDifferences: null,

  // Compass: precise north angle (degrees clockwise from canvas-top to magnetic north)
  // null = not set yet; 0 = north is up; 90 = north is right; etc.
  northAngle: null,
};

/**
 * Load settings from localStorage
 */
function loadSettingsFromStorage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Merge with defaults to handle schema changes
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (error) {
    console.error('Failed to load garden settings from localStorage:', error);
  }
  return DEFAULT_SETTINGS;
}

/**
 * Save settings to localStorage
 */
function saveSettingsToStorage(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save garden settings to localStorage:', error);
  }
}

/**
 * Clear settings from localStorage
 */
function clearSettingsFromStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear garden settings from localStorage:', error);
  }
}

/**
 * Check if settings are configured (has required fields)
 *
 * Note: length/width are derived from the outline polygon and not required
 * as primary configuration fields. Only soilType + closed outline are needed.
 */
function areSettingsConfigured(settings) {
  return (
    settings.soilType !== null &&
    settings.outline !== null &&           // Outline is mandatory in MVP
    settings.outline?.isClosed === true    // Must be closed polygon
  );
}

/**
 * GardenSettingsProvider - Context provider for garden settings
 */
export const GardenSettingsProvider = ({ children }) => {
  // Initialize state from localStorage
  const [settings, setSettings] = useState(() => loadSettingsFromStorage());
  // isConfigured is derived from settings — compute it during render instead of
  // mirroring it into a useState via the auto-save effect.
  const isConfigured = useMemo(() => areSettingsConfigured(settings), [settings]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);

  // Backend persistence state
  const [gardenId, setGardenIdState] = useState(() =>
    localStorage.getItem(ACTIVE_GARDEN_ID_KEY) || null
  );
  // 'idle'|'saving'|'saved'|'error'|'session-expired' ('session-expired' = 401, #679)
  const [saveStatus, setSaveStatus] = useState('idle');
  const [isDirty, setIsDirty] = useState(false);

  // Optimistic-lock version of the active garden (#496). Seeded on load/create
  // and refreshed from every PATCH response so saveToBackend can send If-Match.
  const [gardenVersion, setGardenVersion] = useState(null);

  // Canvas objects most recently loaded from backend. Two jobs, deliberately
  // split since #680:
  //   1. Seed for the next GardenCanvas mount (the editor unmounts whenever the
  //      user visits the dashboard, so this value is what comes back).
  //   2. Trigger for GardenCanvas's externalCanvasData effect — whenever
  //      useGardenLoader fetches a garden (mount, GardenSwitcher,
  //      GardenSwitcherSheet), the canvas refreshes regardless of which
  //      component initiated the load. Fixes #428.
  // Only job 2 needs the nonce below; the save path updates the value alone.
  const [canvasObjects, setCanvasObjectsState] = useState(null);
  const [canvasLoadNonce, setCanvasLoadNonce] = useState(0);

  /**
   * Replace the snapshot *and* request a canvas reload (authoritative load or
   * reset). GardenCanvas keys its reload effect on canvasLoadNonce, so this is
   * the only way to make the live canvas re-read the snapshot.
   */
  const setCanvasObjects = useCallback((objects) => {
    setCanvasObjectsState(objects);
    setCanvasLoadNonce((n) => n + 1);
  }, []);

  /**
   * Keep the snapshot in step with what was just written to the backend —
   * without touching the live canvas (#680).
   *
   * The snapshot used to be written only by useGardenLoader, i.e. once per page
   * load. A save went straight from the Fabric canvas to the backend and left
   * this value at the startup state, so the next editor mount re-applied that
   * stale set and everything placed since the page load vanished from view.
   * Worse, a later edit would then persist the stale set over the good server
   * copy.
   *
   * Bumping the nonce here instead would fix the mount but clear and re-enliven
   * the live canvas after every save — including the silent 30s auto-save,
   * dropping the user's selection mid-edit. Hence value-only.
   */
  const syncCanvasSnapshot = useCallback((objects) => {
    setCanvasObjectsState(objects);
  }, []);

  // Auto-save to localStorage on settings change
  useEffect(() => {
    // Don't save if settings are at default state (indicates a reset)
    const isDefaultState =
      settings.name === 'Mein Garten' &&
      settings.length === null &&
      settings.width === null &&
      settings.soilType === null &&
      settings.drainage === null &&
      settings.orientation === null &&
      settings.sunlight === null &&
      settings.zipCode === null &&
      settings.city === null &&
      settings.backgroundImage === null &&
      settings.outline === null &&
      settings.heightDifferences === null;

    if (!isDefaultState) {
      saveSettingsToStorage(settings);
    }
  }, [settings]);

  /**
   * Set garden ID and persist to localStorage
   */
  const setGardenId = useCallback((id) => {
    setGardenIdState(id);
    if (id) localStorage.setItem(ACTIVE_GARDEN_ID_KEY, id);
    else localStorage.removeItem(ACTIVE_GARDEN_ID_KEY);
  }, []);

  /**
   * Update settings (partial update)
   */
  const updateSettings = useCallback((newSettings) => {
    setSettings((prev) => ({
      ...prev,
      ...newSettings,
    }));
    setIsDirty(true);
  }, []);

  /**
   * Replace all settings (used when wizard completes)
   */
  const setAllSettings = useCallback((newSettings) => {
    setSettings({
      ...DEFAULT_SETTINGS,
      ...newSettings,
    });
  }, []);

  /**
   * Reset settings to defaults
   *
   * Räumt auch den Backend-Canvas-Snapshot ab (#626): Settings und Snapshot
   * gehören zum selben Garten — bleibt der Snapshot stehen, spielt der
   * externalCanvasData-Effekt in GardenCanvas die Objekte des ALTEN Gartens
   * in den neuen ein (Rezidiv von #621 über die Dashboard-Pfade, die sich
   * auf resetSettings verließen statt setCanvasObjects(null) zu rufen).
   */
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    setCanvasObjects(null);
    clearSettingsFromStorage();
  }, [setCanvasObjects]);

  /**
   * Sync settings with backend (future implementation)
   *
   * This will POST settings to /api/v1/gardens/settings
   * For now, it's a placeholder that can be implemented later
   */
  const syncWithBackend = useCallback(async () => {
    if (!isConfigured) {
      console.warn('Cannot sync: Settings not configured');
      return;
    }

    setIsSyncing(true);
    setSyncError(null);

    try {
      // TODO: Implement backend sync when API is ready
      // const response = await fetch('/api/v1/gardens/settings', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(settings),
      // });
      //
      // if (!response.ok) {
      //   throw new Error('Failed to sync settings');
      // }

      console.log('Backend sync placeholder - settings would be synced:', settings);

      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 100));
    } catch (error) {
      console.error('Failed to sync settings with backend:', error);
      setSyncError(error.message);
    } finally {
      setIsSyncing(false);
    }
  }, [settings, isConfigured]);

  /**
   * Get garden area in square meters
   */
  const getGardenArea = useCallback(() => {
    // Prefer the Shoelace polygon area calculated during wizard Step 3
    if (settings.area) return settings.area;
    // Fallback: bounding-box approximation from Step 1 dimensions
    if (settings.length && settings.width) {
      return settings.length * settings.width;
    }
    return 0;
  }, [settings.area, settings.length, settings.width]);

  /**
   * Get settings as query params for plant filtering
   */
  const getPlantFilterParams = useCallback(() => {
    const params = {};

    if (settings.soilType) {
      params.soilType = settings.soilType;
    }

    if (settings.sunlight) {
      params.sunlight = settings.sunlight;
    }

    // Include phValue only if it's been explicitly set (not the default 7.0)
    // This avoids filtering by pH when user hasn't configured it
    if (settings.phValue && settings.phValue !== 7.0) {
      params.phValue = settings.phValue;
    } else if (settings.phValue === 7.0 && settings.soilType) {
      // If soilType is set, user has configured settings, so include pH
      params.phValue = settings.phValue;
    }

    if (settings.drainage) {
      params.drainage = settings.drainage;
    }

    return params;
  }, [settings]);

  /**
   * Convenience method to set garden name
   */
  const setGardenName = useCallback((name) => {
    updateSettings({ name });
  }, [updateSettings]);

  // Context value
  const value = {
    // Current settings
    settings,
    isConfigured,

    // Actions
    updateSettings,
    setAllSettings,
    resetSettings,
    syncWithBackend,

    // Sync state
    isSyncing,
    syncError,

    // Backend persistence
    gardenId,
    setGardenId,
    gardenVersion,
    setGardenVersion,
    saveStatus,
    setSaveStatus,
    isDirty,
    setIsDirty,
    canvasObjects,
    setCanvasObjects,
    canvasLoadNonce,
    syncCanvasSnapshot,

    // Utility functions
    getGardenArea,
    getPlantFilterParams,

    // Convenience accessors
    gardenName: settings.name,
    setGardenName,
  };

  return (
    <GardenSettingsContext.Provider value={value}>
      {children}
    </GardenSettingsContext.Provider>
  );
};

/**
 * useGardenSettings - Hook to access garden settings context
 *
 * Usage:
 * const {
 *   settings,
 *   isConfigured,
 *   updateSettings,
 *   getGardenArea
 * } = useGardenSettings();
 *
 * @throws {Error} If used outside of GardenSettingsProvider
 */
export const useGardenSettings = () => {
  const context = useContext(GardenSettingsContext);
  if (!context) {
    throw new Error('useGardenSettings must be used within a GardenSettingsProvider');
  }
  return context;
};
